/**
 * usePersistedState.ts — useState that mirrors to a StorageBackend.
 *
 * Reads the initial value from storage on mount (falling back to the provided
 * default), and writes back on every change. The setter has the same shape as
 * React's setState (value | updater fn).
 *
 * Usage:
 *   const [theme, setTheme] = usePersistedState(storage, 'theme', 'dark');
 *
 * The hook is intentionally simple — no cross-tab sync, no debouncing. Prefs
 * are small and writes are cheap; if a future pref needs either, split it out.
 */
import { useCallback, useState } from 'react';

import type { StorageBackend } from './StorageBackend';

export function usePersistedState<T>(
  storage: StorageBackend,
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => storage.get<T>(key, defaultValue));

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved =
          typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        storage.set(key, resolved);
        return resolved;
      });
    },
    [storage, key],
  );

  return [value, set];
}
